import { AlertTriangle, Info } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import RiskBadge from './RiskBadge';

type RiskLevel = 'critical' | 'high' | 'medium' | 'low' | 'info';

interface ToolDetail {
  name: string;
  description: string;
  riskLevel: RiskLevel;
  riskReason: string;
  capabilities: string[];
}

interface ToolDetailDialogProps {
  tool: ToolDetail | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ToolDetailDialog = ({ tool, open, onOpenChange }: ToolDetailDialogProps) => {
  if (!tool) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <code className="text-lg font-mono bg-muted px-2 py-1 rounded">{tool.name}</code>
            <RiskBadge level={tool.riskLevel} size="sm" />
          </DialogTitle>
          <DialogDescription>Tool capabilities and risk assessment</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Description
            </h4>
            <p className="text-sm">{tool.description}</p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-risk-medium" />
              Risk Assessment
            </h4>
            <p className="text-sm bg-muted p-3 rounded-lg border-l-4 border-l-risk-medium">
              {tool.riskReason}
            </p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
              <Info className="w-4 h-4 text-risk-info" />
              Capabilities
            </h4>
            <ul className="list-disc list-inside space-y-1">
              {tool.capabilities.map((cap, index) => (
                <li key={index} className="text-sm">
                  {cap}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export type { ToolDetail };
export default ToolDetailDialog;
